import React, { useState, useEffect } from 'react';
import { X, Plus, KeyRound, Trash2, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { listCredentials, createCredential, deleteCredential } from '../../lib/workflowApi';

const CREDENTIAL_TYPES = [
  { value: 'openai', label: 'OpenAI', fields: [{ name: 'api_key', label: 'API Key' }] },
  { value: 'serpapi', label: 'SerpAPI (Web Search)', fields: [{ name: 'api_key', label: 'SerpAPI Key', secret: true }] },
  { value: 'smtp', label: 'SMTP / Gmail', fields: [
    { name: 'host', label: 'SMTP Host', default: 'smtp.gmail.com' },
    { name: 'port', label: 'Port', default: '587' },
    { name: 'username', label: 'Email / Username' },
    { name: 'password', label: 'App Password', secret: true },
  ]},
  { value: 'slack', label: 'Slack', fields: [{ name: 'bot_token', label: 'Bot Token', secret: true }] },
  { value: 'postgresql', label: 'PostgreSQL', fields: [
    { name: 'host', label: 'Host', default: 'localhost' },
    { name: 'port', label: 'Port', default: '5432' },
    { name: 'username', label: 'Username' },
    { name: 'password', label: 'Password', secret: true },
    { name: 'database', label: 'Database' },
  ]},
  { value: 'mysql', label: 'MySQL', fields: [
    { name: 'host', label: 'Host', default: 'localhost' },
    { name: 'port', label: 'Port', default: '3306' },
    { name: 'username', label: 'Username' },
    { name: 'password', label: 'Password', secret: true },
    { name: 'database', label: 'Database' },
  ]},
];

function SecretInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        type={show ? 'text' : 'password'}
        value={value || ''}
        placeholder={placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        style={styles.eyeBtn}
        onClick={() => setShow((v) => !v)}
      >
        {show ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#161B22',
  border: '1px solid #30363D',
  borderRadius: 7,
  padding: '7px 10px',
  color: '#E6EDF3',
  fontSize: 12,
  outline: 'none',
  boxSizing: 'border-box',
};

export default function CredentialManager({ onClose }) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [formType, setFormType] = useState('openai');
  const [formName, setFormName] = useState('');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize form with defaults when type changes
  useEffect(() => {
    const type = CREDENTIAL_TYPES.find(t => t.value === formType);
    if (type) {
      const defaults = {};
      type.fields.forEach(f => {
        if (f.default) defaults[f.name] = f.default;
      });
      setFormData(defaults);
    }
  }, [formType]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listCredentials();
      setCredentials(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectedType = CREDENTIAL_TYPES.find((t) => t.value === formType);

  const handleCreate = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a name for this credential');
      return;
    }
    setSaving(true);
    try {
      await createCredential({ type: formType, name: formName, data: formData });
      toast.success('Credential saved successfully');
      setShowForm(false);
      setFormName('');
      setFormData({});
      load();
    } catch (err) {
      console.error('Failed to save credential:', err);
      toast.error('Failed to save credential. Please check all fields.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this credential?')) return;
    setDeletingId(id);
    try {
      await deleteCredential(id);
      setCredentials((prev) => prev.filter((c) => c.id !== id));
    } catch { /* ignore */ } finally {
      setDeletingId(null);
    }
  };

  const typeColors = {
    openai: '#F87217',
    serpapi: '#06B6D4',
    smtp: '#F59E0B',
    slack: '#10B981',
    postgresql: '#3B82F6',
    mysql: '#06B6D4',
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <ShieldCheck size={17} color="#F87217" />
            <span style={styles.headerTitle}>Credential Manager</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div style={styles.body}>
          {/* SOC2 notice */}
          <div style={styles.securityBadge}>
            <ShieldCheck size={12} color="#10B981" />
            <span>All credentials are encrypted at rest using AES-256 Fernet encryption</span>
          </div>

          {/* Existing credentials */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <span style={styles.sectionTitle}>Stored Credentials</span>
              <button style={styles.addBtn} onClick={() => setShowForm(true)}>
                <Plus size={12} />
                Add New
              </button>
            </div>

            {loading ? (
              <div style={styles.center}>
                <Loader2 size={18} color="#F87217" style={{ animation: 'spin 0.9s linear infinite' }} />
              </div>
            ) : credentials.length === 0 ? (
              <div style={styles.empty}>
                <KeyRound size={26} color="#374151" />
                <p style={{ color: '#64748B', fontSize: 12, margin: '8px 0 0' }}>
                  No credentials stored yet
                </p>
              </div>
            ) : (
              <div style={styles.credList}>
                {credentials.map((cred) => {
                  const color = typeColors[cred.type] || '#94A3B8';
                  return (
                    <div key={cred.id} style={styles.credRow}>
                      <div
                        style={{
                          ...styles.credIcon,
                          background: `${color}18`,
                          border: `1px solid ${color}30`,
                        }}
                      >
                        <KeyRound size={12} color={color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.credName}>{cred.name}</div>
                        <div style={styles.credType}>{cred.type}</div>
                      </div>
                      <button
                        style={styles.deleteBtn}
                        onClick={() => handleDelete(cred.id)}
                        disabled={deletingId === cred.id}
                      >
                        {deletingId === cred.id ? (
                          <Loader2 size={12} style={{ animation: 'spin 0.9s linear infinite' }} />
                        ) : (
                          <Trash2 size={12} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create form */}
          {showForm && (
            <div style={styles.createForm}>
              <div style={styles.sectionTitle}>New Credential</div>

              <div style={styles.formRow}>
                <label style={styles.label}>Type</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={formType}
                  onChange={(e) => { setFormType(e.target.value); setFormData({}); }}
                >
                  {CREDENTIAL_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formRow}>
                <label style={styles.label}>Name</label>
                <input
                  style={inputStyle}
                  placeholder="My OpenAI Key"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>

              {selectedType?.fields.map((field) => (
                <div key={field.name} style={styles.formRow}>
                  <label style={styles.label}>{field.label}</label>
                  {field.secret ? (
                    <SecretInput
                      value={formData[field.name] || ''}
                      onChange={(v) => setFormData((prev) => ({ ...prev, [field.name]: v }))}
                    />
                  ) : (
                    <input
                      style={inputStyle}
                      placeholder={field.default || ''}
                      value={formData[field.name] ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                    />
                  )}
                </div>
              ))}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  style={styles.cancelBtn}
                  onClick={() => { setShowForm(false); setFormName(''); setFormData({}); }}
                >
                  Cancel
                </button>
                <button
                  style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
                  onClick={handleCreate}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={13} style={{ animation: 'spin 0.9s linear infinite' }} /> : <ShieldCheck size={13} />}
                  {saving ? 'Saving…' : 'Save Encrypted'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#0d1117',
    border: '1px solid #1F2937',
    borderRadius: 16,
    width: 480,
    maxWidth: '95vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #1F2937',
    flexShrink: 0,
  },
  headerTitle: { fontSize: 15, fontWeight: 700, color: '#E6EDF3' },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748B',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
  },
  body: { flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  securityBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '8px 12px',
    background: 'rgba(16,185,129,0.07)',
    border: '1px solid rgba(16,185,129,0.2)',
    borderRadius: 8,
    fontSize: 11,
    color: '#10B981',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    background: 'rgba(248, 114, 23, 0.12)',
    border: '1px solid rgba(248, 114, 23, 0.25)',
    borderRadius: 6,
    color: '#A78BFA',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  center: { display: 'flex', justifyContent: 'center', padding: 20 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0' },
  credList: { display: 'flex', flexDirection: 'column', gap: 6 },
  credRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: '#0a0f18',
    border: '1px solid #1F2937',
    borderRadius: 8,
  },
  credIcon: { width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  credName: { fontSize: 13, fontWeight: 600, color: '#C9D1D9' },
  credType: { fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 1 },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#EF4444',
    padding: 5,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.7,
  },
  createForm: {
    padding: 16,
    background: '#0a0f18',
    border: '1px solid #1F2937',
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  formRow: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cancelBtn: {
    flex: 1,
    padding: '8px 12px',
    background: '#161B22',
    border: '1px solid #30363D',
    borderRadius: 7,
    color: '#94A3B8',
    fontSize: 12,
    cursor: 'pointer',
  },
  saveBtn: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 12px',
    background: 'linear-gradient(135deg, #F87217, #EA580C)',
    border: 'none',
    borderRadius: 7,
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  eyeBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748B',
    padding: 0,
  },
};
